import { isServer } from './constant';
import { JumpTypeEnum } from "@/types/jumpPage";
import jumpPage from "./bridge/jumpPage";
import openMiniProgram, { OpenMiniProgramEnvEnum } from "./bridge/openMiniProgram";
import openPage from "./bridge/openPage";
import { RUNTIME_ENV_TYPE } from "./constant";
import { getRuntimeEnv, getAppEnv, getUA } from "./getBrowserEnv";
import { Level, reportCustomMetric, reportError } from "./owl";
import windcall from "./windcall";
import { downloadMeituanApp } from "./downloadApp";
import getUnpl from "./getUnpl";
import { logInfo } from './logan';
import { getUnplStatus } from './ptcToUnplConverter';

export interface JumpConfig {
  source: string; // 跳转来源，用于上报跳转成功率
  jumpLink: string;
  jumpType: JumpTypeEnum;
  isRedirect?: boolean; // 是否重定向，默认 false
  sourceBid?: string; // 唤端使用来源 mc bid
  // autoScheme?: boolean; // 是否自动拼接 imeituan 协议链接，在 iOS / Android 跳转 takeout 容器，在 Harmony 跳转 knb 容器
  miniProgramId?: string; // 小程序原始 ID，其它环境跳转小程序使用
  miniProgramType?: OpenMiniProgramEnvEnum; // 跳转小程序包类型（开发版、体验版、正式版），其它环境跳转小程序使用
  onSuccess?: () => void;
  onFail?: (err: any) => void;
}

interface JumpMethods {
  // 跳转 H5 链接
  jumpH5: (config: JumpConfig) => void;
  // 跳转 APP 协议链接
  jumpScheme: (config: JumpConfig) => void;
  // 跳转小程序页面
  jumpMiniProgram: (config: JumpConfig) => void;
}

type JumpPageErrMsg = "success" | "jumpLinkEmpty" | "jsSDKUnavailable" | "jsSDKFail" | "jumpFail" | "unsupportJumpType" | "unsupportEnv" | "jumpLinkInvalid";

const appEnv = getAppEnv();
const PageCid = isServer ? "" : window?.PAGE_CID || "";

/**
 * 上报跳转成功率
 * @param isSuccess 是否成功
 * @param jumpMethod 跳转方法
 * @param jumpType 跳转类型
 * @param errMsg 错误信息，默认 success
 * @param source 跳转来源，用于上报跳转成功率
 */
const reportJumpPageSuccessRate = (
  isSuccess: boolean,
  jumpMethod: keyof JumpMethods,
  errMsg: JumpPageErrMsg = "success",
  source: string,
) => {
  reportCustomMetric({
    key: "jumpPageSuccessRate",
    value: isSuccess ? 1 : 0,
    tags: {
      jumpMethod: jumpMethod,
      errMsg,
      source,
      // 获取 unpl 相关状态
      ...getUnplStatus(),
    },
  });
};

const getAppScheme = (jumpLink: string) => {
  if (!jumpLink) return;
  if (jumpLink.startsWith("imeituan://")) {
    return jumpLink;
  } else {
    return appEnv.isHarmonyOS
      ? `imeituan://www.meituan.com/knb?url=${encodeURIComponent(jumpLink)}`
      : `imeituan://www.meituan.com/takeout/browser?inner_url=${encodeURIComponent(jumpLink)}`
  }
}

const getAppSchemeWithChannel = (jumpLink: string, sourceBid: string = PageCid) => {
  if (!jumpLink) return;
  // lch 渠道：https://km.sankuai.com/collabpage/2646944284
  // 投放形式：0-微信环境，1-非微信环境
  const currentRuntimeEnv = getRuntimeEnv();
  const distributeType = currentRuntimeEnv === RUNTIME_ENV_TYPE.WX_BROWSER ? "0" : "1";
  const unpl = getUnpl();
  const lch = `lch=cps:mtlm:${distributeType}:fxcenter:deeplink:default:${sourceBid}:${unpl}`;
  if (jumpLink.startsWith("imeituan://")) {
    const [jumpLinkPath, jumpLinkQuery] = jumpLink.split("?");
    if (jumpLinkQuery) {
      const jumpLinkQueryParams = new URLSearchParams(jumpLinkQuery);
      if (!jumpLinkQueryParams.has("lch")) {
        return `${jumpLinkPath}?${jumpLinkQueryParams.toString()}&${lch}`
      } else {
        // 运营配置了 lch 参数，不覆盖
        return jumpLink;
      }
    } else {
      return `${jumpLinkPath}?${lch}`;
    }
  } else {
    return appEnv.isHarmonyOS
      ? `imeituan://www.meituan.com/knb?url=${encodeURIComponent(jumpLink)}&${lch}`
      : `imeituan://www.meituan.com/takeout/browser?inner_url=${encodeURIComponent(jumpLink)}&${lch}`
  }
}

/**
 * jumpH5 工厂方法（美团小程序 & 浏览器环境共用）
 * @param errorName 错误上报名称，用于错误上报标识
 * @param timeoutErrorLevel 超时错误的上报级别，默认 WARN
 */
const createJumpH5 = (errorName: string) => (config: JumpConfig) => {
  if (!config?.jumpLink) {
    if (typeof config.onFail === "function") {
      config.onFail("跳转链接为空");
    }
    reportError({
      name: `[jumpToPage] ${errorName}`,
      msg: `跳转链接为空`,
      category: 'baseError',
      level: Level.ERROR,
      tags: { source: config.source },
    })
    reportJumpPageSuccessRate(false, "jumpH5", "jumpLinkEmpty", config.source);
    return;
  }
  if (!config.jumpLink.startsWith("http")) {
    if (typeof config.onFail === "function") {
      config.onFail("跳转链接无效");
    }
    reportError({
      name: `[jumpToPage] ${errorName}`,
      msg: `跳转链接无效`,
      category: 'baseError',
      level: Level.ERROR,
      tags: { source: config.source },
    })
    reportJumpPageSuccessRate(false, "jumpH5", "jumpLinkInvalid", config.source);
    return;
  }
  // 超时检测：如果 3s 内页面未跳转，上报超时错误
  window.setTimeout(() => {
    if (typeof config.onFail === "function") {
      config.onFail("jumpH5Timeout");
    }
    reportError({
      name: `[jumpToPage] ${errorName}`,
      msg: `jumpH5 超时，页面未离开`,
      category: 'baseError',
      level: Level.WARN,
      tags: { source: config.source },
    });
  }, 3000);
  if (config.isRedirect) {
    reportJumpPageSuccessRate(true, "jumpH5", "success", config.source);
    location.replace(config.jumpLink);
  } else {
    reportJumpPageSuccessRate(true, "jumpH5", "success", config.source);
    location.href = config.jumpLink;
  }
};

/**
 * 美团 APP 环境跳转方法
 */
const JumpMethodsInMtApp: JumpMethods = {
  jumpH5: (config: JumpConfig) => {
    JumpMethodsInMtApp.jumpScheme(config);
  },
  jumpScheme: (config: JumpConfig) => {
    // 无跳转链接
    if (!config?.jumpLink) {
      if (typeof config.onFail === "function") {
        config.onFail("跳转链接为空");
      }
      reportError({
        name: "[jumpToPage] jumpSchemeInMtAppError",
        msg: `跳转链接为空`,
        category: 'baseError',
        level: Level.ERROR,
        tags: { source: config.source },
      })
      reportJumpPageSuccessRate(false, "jumpScheme", "jumpLinkEmpty", config.source);
      return;
    }

    // 调用桥方法的参数
    const jumpScheme = getAppScheme(config.jumpLink);
    const jumpConfig = {
      url: jumpScheme!,
      onSuccess: () => {
        if (typeof config.onSuccess === "function") {
          config.onSuccess();
        }
        logInfo(`[jumpToPage] jumpSchemeInMtApp success`);
        reportJumpPageSuccessRate(true, "jumpScheme", "success", config.source);
      },
      onFail: (err: any) => {
        if (typeof config.onFail === "function") {
          config.onFail(err);
        }
        reportError({
          name: "[jumpToPage] jumpSchemeInMtAppError",
          msg: `跳转失败: ${err}`,
          category: 'baseError',
          level: Level.WARN,
          tags: { source: config.source },
        })
        reportJumpPageSuccessRate(false, "jumpScheme", "jumpFail", config.source);
      },
    };
    logInfo(`[jumpToPage] jumpSchemeInMtApp jumpScheme: ${jumpScheme}`);
    // 外投容器不支持重定向跳转
    if (config.isRedirect && !appEnv.isGrowthContainer) {
      jumpPage(jumpConfig);
    } else {
      openPage(jumpConfig);
    }
  },
  jumpMiniProgram: (config: JumpConfig) => {
    // 无跳转链接
    if (!config?.jumpLink || !config?.miniProgramId) {
      if (typeof config.onFail === "function") {
        config.onFail("跳转链接为空");
      }
      reportError({
        name: "[jumpToPage] jumpMiniProgramInMtAppError",
        msg: `跳转链接为空`,
        category: 'baseError',
        level: Level.ERROR,
        tags: { source: config.source },
      })
      reportJumpPageSuccessRate(false, "jumpMiniProgram", "jumpLinkEmpty", config.source);
      return;
    }

    openMiniProgram({
      path: config.jumpLink,
      miniProgramId: config.miniProgramId,
      type: config.miniProgramType,
      onSuccess: () => {
        if (typeof config.onSuccess === "function") {
          config.onSuccess();
        }
        reportJumpPageSuccessRate(true, "jumpMiniProgram", "success", config.source);
      },
      onFail: (err: any) => {
        if (typeof config.onFail === "function") {
          config.onFail(err);
        }
        reportError({
          name: "[jumpToPage] jumpMiniProgramInMtAppError",
          msg: `跳转失败: ${err}`,
          category: 'baseError',
          level: Level.WARN,
          tags: { source: config.source },
        })
        reportJumpPageSuccessRate(false, "jumpMiniProgram", "jumpFail", config.source);
      },
    })
  },
};

/**
 * 美团小程序环境跳转方法
 */
const JumpMethodsInMtWxApp: JumpMethods = {
  jumpH5: createJumpH5('jumpH5InMtWxAppError'),
  jumpScheme: (config: JumpConfig) => {
    // 小程序不支持跳转协议链接
    if (typeof config?.onFail === "function") {
      config.onFail("小程序不支持跳转 APP");
    }
    reportError({
      name: "[jumpToPage] jumpSchemeInMtWxAppError",
      msg: `小程序不支持跳转 APP`,
      category: 'baseError',
      level: Level.ERROR,
      tags: { source: config.source },
    })
    reportJumpPageSuccessRate(false, "jumpScheme", "unsupportJumpType", config.source);
  },
  jumpMiniProgram: (config: JumpConfig) => {
    // 跳转小程序页面：使用 wx.navigateTo 方法，不支持跳转其它小程序
    // 跳转链接为空
    if (!config?.jumpLink) {
      if (typeof config.onFail === "function") {
        config.onFail("跳转链接为空");
      }
      reportError({
        name: "[jumpToPage] jumpMiniProgramInMtWxAppError",
        msg: `跳转链接为空`,
        category: 'baseError',
        level: Level.ERROR,
        tags: { source: config.source },
      })
      reportJumpPageSuccessRate(false, "jumpMiniProgram", "jumpLinkEmpty", config.source);
      return;
    }
    // TODO：判断链接是否为 Tab 页，使用 switchTab，确认是否需要支持
    if (config.isRedirect) {
      // 使用 wx.redirectTo 方法跳转小程序页面
      if (typeof window.wx?.miniProgram?.redirectTo !== "function") {
        if (typeof config.onFail === "function") {
          config.onFail("wx.miniProgram.redirectTo 不可用");
        }
        reportError({
          name: "[jumpToPage] jumpMiniProgramInMtWxAppError",
          msg: `wx.miniProgram.redirectTo 不可用`,
          category: 'baseError',
          level: Level.WARN,
          tags: { source: config.source },
        })
        reportJumpPageSuccessRate(false, "jumpMiniProgram", "jsSDKUnavailable", config.source);
        return;
      }
      logInfo(`[jumpToPage] jumpMiniProgramInMtWxApp redirectTo: ${config.jumpLink}`);
      window.wx.miniProgram.redirectTo({
        url: config.jumpLink,
        success: () => {
          if (typeof config.onSuccess === "function") {
            config.onSuccess();
          }
          reportJumpPageSuccessRate(true, "jumpMiniProgram", "success", config.source);
        },
        fail: (err) => {
          if (typeof config.onFail === "function") {
            config.onFail(`wx.redirectTo 跳转失败: ${err}`);
          }
          reportError({
            name: "[jumpToPage] jumpMiniProgramInMtWxAppError",
            msg: `wx.redirectTo 跳转失败: ${err}`,
            category: 'baseError',
            level: Level.WARN,
            tags: { source: config.source },
          })
          reportJumpPageSuccessRate(false, "jumpMiniProgram", "jsSDKFail", config.source);
        },
      });
    } else {
      // 使用 wx.navigateTo 方法跳转小程序页面
      if (typeof window.wx?.miniProgram?.navigateTo !== "function") {
        if (typeof config.onFail === "function") {
          config.onFail("wx.miniProgram.navigateTo 不可用");
        }
        reportError({
          name: "[jumpToPage] jumpMiniProgramInMtWxAppError",
          msg: `wx.miniProgram.navigateTo 不可用`,
          category: 'baseError',
          level: Level.WARN,
          tags: { source: config.source },
        })
        reportJumpPageSuccessRate(false, "jumpMiniProgram", "jsSDKUnavailable", config.source);
        return;
      }
      logInfo(`[jumpToPage] jumpMiniProgramInMtWxApp navigateTo: ${config.jumpLink}, miniProgramId: ${config.miniProgramId}`);
      window.wx.miniProgram.navigateTo({
        url: config.jumpLink,
        success: () => {
          if (typeof config.onSuccess === "function") {
            config.onSuccess();
          }
          reportJumpPageSuccessRate(true, "jumpMiniProgram", "success", config.source);
        },
        fail: (err) => {
          if (typeof config.onFail === "function") {
            config.onFail(`wx.navigateTo 跳转失败: ${err}`);
          }
          reportError({
            name: "[jumpToPage] jumpMiniProgramInBrowserError",
            msg: `wx.navigateTo 跳转失败: ${err}`,
            category: 'baseError',
            level: Level.WARN,
            tags: { source: config.source },
          })
          reportJumpPageSuccessRate(false, "jumpMiniProgram", "jsSDKFail", config.source);
        },
      });
    }
  },
};

/**
 * 浏览器环境跳转方法
 */
const JumpMethodsInBrowser: JumpMethods = {
  jumpH5: createJumpH5('jumpH5InBrowserError'),
  jumpScheme: (config: JumpConfig) => {
    if (!config.jumpLink) {
      if (typeof config.onFail === "function") {
        config.onFail("跳转链接为空");
      }
      reportError({
        name: "[jumpToPage] jumpSchemeInBrowserError",
        msg: `跳转链接为空`,
        category: 'baseError',
        level: Level.ERROR,
        tags: { source: config.source },
      })
      reportJumpPageSuccessRate(false, "jumpScheme", "jumpLinkEmpty", config.source);
      return;
    }

   const jumpLink = getAppSchemeWithChannel(config.jumpLink, config.sourceBid);
   logInfo(`[jumpToPage] jumpSchemeInBrowser windcall.callApp: ${jumpLink}`);
   console.log("[jumpScheme] jumpLink", jumpLink);
    // 浏览器环境跳转协议链接：使用风信 SDK
    // https://km.sankuai.com/collabpage/2664945973#b-23ecb32a15524af892d3b3a6ab970301
    windcall.callApp({
      url: jumpLink,
      success: () => {
        if (typeof config.onSuccess === "function") {
          config.onSuccess();
        }
        reportJumpPageSuccessRate(true, "jumpScheme", "success", config.source);
      },
      fail: () => {
        if (typeof config.onFail === "function") {
          config.onFail("windcall.callApp 跳转失败");
        }
        reportError({
          name: "[jumpToPage] jumpSchemeInBrowserError",
          msg: `windcall.callApp 跳转失败`,
          category: 'baseError',
          level: Level.WARN,
          tags: { source: config.source },
        })
        reportJumpPageSuccessRate(false, "jumpScheme", "jumpFail", config.source);
        // 兜底跳转下载链接
        downloadMeituanApp();
      },
    });
  },
  jumpMiniProgram: () => {
    // 跳转小程序页面：获取 URL Link 跳转
    // TODO 有需要再实现，有接口可用
  },
};

export function getJumpMethodKey(config: JumpConfig): keyof JumpMethods {
  if ([JumpTypeEnum.H5].includes(config.jumpType)) {
    return "jumpH5";
  } else if ([JumpTypeEnum.MT_APP].includes(config.jumpType)) {
    return "jumpScheme";
  } else if ([JumpTypeEnum.MT_WXAPP].includes(config.jumpType)) {
    return "jumpMiniProgram";
  }
  return "jumpH5";
}

/**
 * 跳转页面
 * SSR兼容性：服务端渲染时跳过跳转逻辑
 * @param config 跳转配置
 */
function jumpToPage(config: JumpConfig) {
  // SSR兼容性：服务端渲染时跳过跳转逻辑
  if (isServer) {
    console.warn('[jumpPage] Page jump is not supported on server side');
    return;
  }

  // 1. 获取当前环境类型
  const currentEnv = getRuntimeEnv();
  const currentUA = getUA();
  // 2. 获取跳转方法
  const jumpMethodKey = getJumpMethodKey(config);
  logInfo(`[jumpToPage] startJump! currentEnv: ${currentEnv}, jumpMethodKey: ${jumpMethodKey}, config: ${JSON.stringify(config)}`);
  // 3. 调用相应的跳转方法
  switch (currentEnv) {
    case RUNTIME_ENV_TYPE.MT_MINI:
      JumpMethodsInMtWxApp[jumpMethodKey](config);
      break;
    case RUNTIME_ENV_TYPE.MT_APP:
      JumpMethodsInMtApp[jumpMethodKey](config);
      break;
    case RUNTIME_ENV_TYPE.WX_BROWSER:
    case RUNTIME_ENV_TYPE.BROWSER:
      JumpMethodsInBrowser[jumpMethodKey](config);
      break;
    default:
      // 不支持的环境类型
      reportError({
        name: "[jumpToPage] unsupportEnv",
        msg: `不支持的跳转环境类型, userAgent: ${currentUA}`,
        category: 'baseError',
        level: Level.WARN,
        tags: { source: config.source },
      })
      reportJumpPageSuccessRate(false, jumpMethodKey, "unsupportEnv", config.source);
      break;
  }
}

export {
  JumpTypeEnum, // 跳转类型
  JumpMethodsInMtWxApp, // 美团小程序环境跳转方法
  JumpMethodsInMtApp, // 美团 APP 环境跳转方法
  JumpMethodsInBrowser, // 浏览器环境跳转方法
  jumpToPage, // 通用跳转页面方法
};
